import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import HomeService from "../../services/HomeService";
import Logger from "../../services/Logger";
import { trackEvent } from "../../services/GoogleAnalytics";
import { getHomePuzzleLandingFeaturedMeta } from "../browse-puzzles/homePuzzleLandingUtils";
import { usePagesTranslation } from "../locales";

const CARD_COUNT = 3;

/** Difficulty chip label + color class. */
function difficultyTag(level, t) {
    const key = String(level || "").toUpperCase();
    if (key === "EASY") {
        return { label: t("puzzle.card.difficulty-easy", "Easy"), className: "pi-tag--easy" };
    }
    if (key === "HARD") {
        return { label: t("puzzle.card.difficulty-hard", "Hard"), className: "pi-tag--hard" };
    }
    return { label: t("puzzle.card.difficulty-medium", "Medium"), className: "pi-tag--medium" };
}

function puzzleTypeLabel(type, t) {
    const key = String(type || "").toUpperCase();
    if (key === "CROSSWORD") return t("nav.crossword", "Crossword");
    if (key === "HIDDEN_MESSAGE") return t("nav.hidden-msg", "Hidden Message");
    return t("nav.word-search", "Word Search");
}

/** Play route for a `/home/cache/puzzles` row, which carries no play path of its own. */
function cachePuzzlePlayPath(puzzle) {
    const type = String(puzzle?.type || "").toUpperCase();
    if (!puzzle?.id) return null;
    if (type === "CROSSWORD") return `/puzzle/play-cross/${puzzle.id}`;
    if (type === "HIDDEN_MESSAGE") return `/puzzle/play-hidden-message/${puzzle.id}`;
    return `/puzzle/play-word-search/${puzzle.id}`;
}

/** Swap Crossword and Hidden Message cards; leave Word Search in place. */
function swapCrosswordAndHiddenMessage(list) {
    const items = [...list];
    const iCw = items.findIndex((p) => String(p.type || "").toUpperCase() === "CROSSWORD");
    const iHm = items.findIndex((p) => String(p.type || "").toUpperCase() === "HIDDEN_MESSAGE");
    if (iCw >= 0 && iHm >= 0) {
        const tmp = items[iCw];
        items[iCw] = items[iHm];
        items[iHm] = tmp;
    }
    return items;
}

const DAILY_TYPES = ["CROSSWORD", "WORD_SEARCH", "HIDDEN_MESSAGE"];

/** Word-grid cards show the type only. Difficulty is not a level the player picks. */
function hidesDifficulty(type) {
    return DAILY_TYPES.includes(String(type || "").toUpperCase());
}

async function newestMetaForType(language, puzzleType) {
    const page = await HomeService.getHomePuzzlePage({
        page: 0,
        pageSize: 4,
        language,
        puzzleType,
        accessFilter: "free",
    });
    const metas = (page?.content ?? [])
        .map((row) => getHomePuzzleLandingFeaturedMeta(row, language))
        .filter(Boolean);
    return metas[0] ?? null;
}

async function loadDailyPuzzles(language) {
    const results = await Promise.all(
        DAILY_TYPES.map(async (puzzleType) => {
            try {
                return await newestMetaForType(language, puzzleType);
            } catch (error) {
                Logger.error(`Error loading daily ${puzzleType}:`, error);
                return null;
            }
        }),
    );
    const picked = results.filter(Boolean);
    if (picked.length > 0) {
        return swapCrosswordAndHiddenMessage(picked);
    }

    // Cached daily trio: no story art, so those cards fall back to a patterned thumb.
    const cached = await HomeService.getHomePuzzles(language);
    return swapCrosswordAndHiddenMessage(
        (Array.isArray(cached) ? cached : [])
            .map((puzzle) => {
                const playPath = cachePuzzlePlayPath(puzzle);
                if (!playPath) return null;
                return {
                    playId: puzzle.id,
                    title: puzzle.title,
                    level: puzzle.difficultyLevel,
                    type: puzzle.type,
                    imageUrl: null,
                    playPath,
                };
            })
            .filter(Boolean)
            .slice(0, CARD_COUNT)
    );
}

function SkeletonCard() {
    return (
        <div className="pi-card" aria-hidden>
            <div className="pi-skeleton pi-skeleton--thumb" />
            <div className="pi-skeleton pi-skeleton--line" style={{ width: "85%" }} />
            <div className="pi-skeleton pi-skeleton--line" style={{ width: "60%" }} />
            <div className="pi-skeleton pi-skeleton--btn" />
        </div>
    );
}

/** Daily free web tiers — playable in the browser, no install and no sign-up. */
export default function LandingDailyPlaySection({ contentLanguage }) {
    const { t } = usePagesTranslation();
    const [puzzles, setPuzzles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const list = await loadDailyPuzzles(contentLanguage);
                if (!cancelled) setPuzzles(list);
            } catch (error) {
                Logger.error("Error loading daily landing puzzles:", error);
                if (!cancelled) setPuzzles([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [contentLanguage]);

    return (
        <section className="pi-section" id="daily-free-play" aria-labelledby="pi-daily-title">
            <div className="pi-landing__inner">
                <header className="pi-section__head">
                    <p className="pi-section__kicker">
                        {t("landingApp.daily-kicker", "No install needed")}
                    </p>
                    <h2 className="pi-section__title" id="pi-daily-title">
                        {t("landingApp.daily-title", "Daily Free Play")}
                    </h2>
                    <p className="pi-section__subtitle">
                        {t(
                            "landingApp.daily-subtitle",
                            "Fresh word puzzles in your browser every day — free tiers, no sign-up to start.",
                        )}
                    </p>
                    <p className="pi-section__pdf-note">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2m-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5zm4-3H19v1h1.5V11H19v2h-1.5V7h3zM9 9.5h1v-1H9zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4zm10 5.5h1v-3h-1z" />
                        </svg>
                        {t(
                            "puzzles-browse.pdf-offline-note",
                            "Download PDF is available — print it and play offline.",
                        )}
                    </p>
                </header>

                {loading ? (
                    <div className="pi-grid">
                        {Array.from({ length: CARD_COUNT }, (_, i) => (
                            <SkeletonCard key={i} />
                        ))}
                    </div>
                ) : puzzles.length === 0 ? (
                    <p className="pi-daily__empty">
                        {t(
                            "landingApp.daily-empty",
                            "Today's free puzzles are being prepared. Browse the full library in the meantime.",
                        )}
                    </p>
                ) : (
                    <div className="pi-grid">
                        {puzzles.map((puzzle) => {
                            const tag = hidesDifficulty(puzzle.type)
                                ? null
                                : difficultyTag(puzzle.level, t);
                            return (
                                <article className="pi-card" key={puzzle.playId}>
                                    <div className="pi-daily__thumb">
                                        {puzzle.imageUrl ? (
                                            <img
                                                src={puzzle.imageUrl}
                                                alt=""
                                                loading="lazy"
                                                decoding="async"
                                            />
                                        ) : (
                                            <div className="pi-daily__thumb-fallback">
                                                {puzzleTypeLabel(puzzle.type, t)}
                                            </div>
                                        )}
                                        <div className="pi-daily__tags">
                                            <span className="pi-tag">
                                                {puzzleTypeLabel(puzzle.type, t)}
                                            </span>
                                            {tag ? (
                                                <span className={`pi-tag ${tag.className}`}>
                                                    {tag.label}
                                                </span>
                                            ) : null}
                                        </div>
                                    </div>
                                    <h3 className="pi-daily__title">{puzzle.title}</h3>
                                    <div className="pi-card__spacer" />
                                    <RouterLink
                                        className="pi-btn pi-btn--brand"
                                        to={puzzle.playPath}
                                        onClick={() =>
                                            trackEvent("clicked", {
                                                name: "LandingSolveNow",
                                                type: puzzle.type,
                                            })
                                        }
                                    >
                                        {t("landingApp.solve-cta", "Solve Now")}
                                    </RouterLink>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
}
